import React from 'react';
import { Building2 } from 'lucide-react';
import { cn } from '../../lib/utils';

// Shared sender identity primitives for AI Chat, Signal Builder, and future chat interfaces.
export const ORA_SENDER_LABEL = 'ORA';
export const USER_SENDER_LABEL = 'You';

type ChatRole = 'assistant' | 'user';

interface ChatUserLike {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

const buildInitialsFromText = (value: string): string => {
  const normalized = value
    .trim()
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ');

  if (!normalized) return 'U';

  const parts = normalized.split(' ').filter(Boolean);
  if (parts.length === 1) {
    const alphanumeric = parts[0].replace(/[^a-zA-Z0-9]/g, '');
    return (alphanumeric.slice(0, 2) || 'U').toUpperCase();
  }

  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase() || 'U';
};

export const getChatUserInitials = (user?: ChatUserLike | null): string => {
  if (!user) return 'U';

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (fullName) return buildInitialsFromText(fullName);

  if (user.firstName) return buildInitialsFromText(user.firstName);

  const emailPrefix = user.email?.split('@')[0]?.trim();
  if (emailPrefix) return buildInitialsFromText(emailPrefix);

  return 'U';
};

interface ChatMessageAvatarProps {
  role: ChatRole;
  userInitials?: string;
  className?: string;
}

export const ChatMessageAvatar: React.FC<ChatMessageAvatarProps> = ({ role, userInitials, className }) => {
  return (
    <div
      className={cn(
        'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border font-semibold',
        role === 'assistant'
          ? 'border-border bg-muted text-[9px] text-muted-foreground tracking-[0.08em]'
          : 'border-transparent bg-primary text-[11px] text-primary-foreground',
        className
      )}
    >
      {role === 'assistant' ? (
        <Building2 className="h-3.5 w-3.5" />
      ) : (
        buildInitialsFromText(userInitials || 'U')
      )}
    </div>
  );
};

interface ChatSenderLabelProps {
  role: ChatRole;
  className?: string;
}

export const ChatSenderLabel: React.FC<ChatSenderLabelProps> = ({ role, className }) => (
  <span className={cn('px-1 text-xs font-medium text-muted-foreground', className)}>
    {role === 'assistant' ? ORA_SENDER_LABEL : USER_SENDER_LABEL}
  </span>
);
